import {
  DotsVerticalIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  DropdownMenu,
  Label,
  Link,
  Select,
  SkeletonLoader,
  Spinner,
  Title,
} from '@yoast/ui-library';
import cx from 'classnames';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

// Shared localStorage key to track Perplexity tooltip dismissal
const PERPLEXITY_TOOLTIP_DISMISSED_KEY = 'perplexity_tooltip_dismissed';

// Target date for Perplexity popover expiration (created once)
const PERPLEXITY_POPOVER_EXPIRATION_DATE = new Date('2026-01-13');

// Shared localStorage key to track Claude tooltip dismissal
const CLAUDE_TOOLTIP_DISMISSED_KEY = 'claude_tooltip_dismissed';

// Target date for Claude popover expiration (created once)
const CLAUDE_POPOVER_EXPIRATION_DATE = new Date('2026-08-01');

import {
  getGetAnalysisListQueryKey,
  getGetAnalysisQueryKey,
  getGetBrandQueryKey,
  useDeleteScan,
  useGetAnalysis,
  useGetAnalysisList,
  useGetBrand,
  useGetBrands,
  useGetCurrentUser,
} from '../../api/generated/insights';
import {
  AIProviderEnum,
  AnalysisStatus,
  ScanStatus,
} from '../../api/generated/insights.schemas';
import YoastIconSmall from '../../assets/Yoast-icon-small.svg?react';
import AlertFinishBrandSetup from '../../components/AlertFinishBrandSetup';
import AlertStartTrialAnalysis from './AlertStartTrialAnalysis';
import createComponent from '../../components/CreateComponent';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import Divider from '../../components/Divider';
import ModalUpgradeToAdd from '../../components/ModalUpgradeToAdd';
import AnalysisDateSelect from '../../components/UI/AnalysisDateSelect';
import Popover from '../../components/UI/Popover';
import {
  useBrandAnalysisData,
  useBrandAnalysisMutations,
} from '../../contexts';
import useScan from '../../hooks/useScan';
import { generateHashedLegalKey } from '../../utils/legalStorageUtils';
import { getAIProviderLabel } from '../../utils/stringUtils';
import { formatScheduledAnalysisDate } from '../../utils/timeUtils';
import IncompleteAnalysisAlert from './IncompleteAnalysisAlert';
import IncompleteAnalysisMessage from './IncompleteAnalysisMessage';
import { getDismissKey, getNotificationType } from './scanRetryUtils';

interface HeaderCardProps {
  className?: string;
}

const SelectDiv = createComponent`yst-flex-1 yst-space-y-2`;

// Helper function to generate legacy key for backward compatibility
const generateLegacyKey = (brandId: string, analysisId: string): string => {
  return `yst_legal_alert_dismissed_${brandId}_${analysisId}`;
};

const HeaderCard: React.FC<HeaderCardProps> = ({ className }) => {
  const { brandId, analysisId, aiProvider } = useBrandAnalysisData();
  const { setAiProvider, setAnalysisId } = useBrandAnalysisMutations();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [perplexityPopoverDismissed, setPerplexityPopoverDismissed] = useState(
    () => localStorage.getItem(PERPLEXITY_TOOLTIP_DISMISSED_KEY) === 'true',
  );
  const [claudePopoverDismissed, setClaudePopoverDismissed] = useState(
    () => localStorage.getItem(CLAUDE_TOOLTIP_DISMISSED_KEY) === 'true',
  );

  const { data: selectedScanData } = useScan();

  // GET User
  const { data: userResponse, isLoading: isLoadingUser } = useGetCurrentUser({
    query: {
      select: ({ data }) => data,
    },
  });
  const user = userResponse?.data;
  const currentUserDisplayName = user?.displayName;

  // GET Brands
  const { data: brands, isLoading: isLoadingBrands } = useGetBrands({
    query: {
      select: ({ data }) => data?.brands,
    },
  });

  // GET Analysis List
  const { data: analysisList, isLoading: isLoadingAnalysisList } =
    useGetAnalysisList(brandId, {
      query: {
        enabled: !!brandId,
        select: ({ data }) => data?.analyses,
      },
    });

  // GET Analysis Detail
  const { data: analysisData, isLoading: isLoadingAnalysis } = useGetAnalysis(
    brandId,
    analysisId,
    {
      query: {
        enabled: !!brandId && !!analysisId,
        select: ({ data }) => data,
      },
    },
  );

  // Use server-provided scan arrays
  const completedScans = useMemo(
    () => analysisData?.analysis?.completedScans ?? [],
    [analysisData?.analysis?.completedScans],
  );
  const failedScans = analysisData?.analysis?.failedScans ?? [];
  const pendingScans = analysisData?.analysis?.pendingScans ?? [];

  // --- INCOMPLETE ANALYSIS ALERT DISMISS LOGIC ---
  const incompleteNotificationType = getNotificationType(failedScans);
  const incompleteDismissKey = getDismissKey(analysisId, incompleteNotificationType);
  const [isAlertDismissed, setIsAlertDismissed] = useState(
    () => localStorage.getItem(incompleteDismissKey) !== null,
  );

  // Reset dismiss state when the dismiss key changes (e.g. different analysis or notification type)
  useEffect(() => {
    setIsAlertDismissed(localStorage.getItem(incompleteDismissKey) !== null);
  }, [incompleteDismissKey]);

  const handleIncompleteAlertDismiss = () => {
    localStorage.setItem(incompleteDismissKey, 'true');
    setIsAlertDismissed(true);
  };

  const hasPerplexityScanAvailableToShow = completedScans.some(
    (scan) => scan.aiProvider === AIProviderEnum.perplexity,
  );

  const hasClaudeScanAvailableToShow = completedScans.some(
    (scan) => scan.aiProvider === AIProviderEnum.anthropic,
  );

  // Cache current date to avoid creating new Date objects on every render
  const currentDate = useMemo(() => new Date(), []);

  const showPerplexityPopover =
    // Check if current date is before January 13, 2026
    currentDate < PERPLEXITY_POPOVER_EXPIRATION_DATE &&
    // - [x] user has not manually dismissed the popover
    !perplexityPopoverDismissed &&
    //  - [x] current AI provider is not perplexity
    selectedScanData?.aiProvider !== AIProviderEnum.perplexity &&
    // - [x] Check to ensure there is a scan of type perplexity available to show
    !!hasPerplexityScanAvailableToShow;

  const showClaudePopover =
    // Check if current date is before August 1, 2026
    currentDate < CLAUDE_POPOVER_EXPIRATION_DATE &&
    // user has not manually dismissed the popover
    !claudePopoverDismissed &&
    // current AI provider is not Claude
    selectedScanData?.aiProvider !== AIProviderEnum.anthropic &&
    // Check to ensure there is a scan of type Claude available to show
    !!hasClaudeScanAvailableToShow;


  // GET Brand Queries
  // const { data: brandQueries, isLoading: isLoadingQueries } =
  //   useGetBrandQueries(brandId, {
  //     query: {
  //       enabled: !!brandId,
  //       select: ({ data }) => data,
  //     },
  //   });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  // DELETE Scan
  const { mutate: deleteScan, isPending: isDeletingScan } = useDeleteScan({
    mutation: {
      onSuccess: async () => {
        // Store current state for navigation decisions
        const currentAnalysis = analysisData?.analysis;
        const currentCompletedScans = currentAnalysis?.completedScans || [];
        const currentAnalysisList = analysisList || [];

        // Helper function to invalidate cache
        const invalidateCache = () =>
          Promise.all([
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisListQueryKey(brandId),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetAnalysisQueryKey(brandId, analysisId),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetBrandQueryKey(brandId),
            }),
          ]);

        // Close modal immediately
        setShowDeleteModal(false);

        // Early return for missing analysis
        if (!currentAnalysis) {
          navigate(`/brands/${brandId}`);
          await invalidateCache();
          return;
        }

        // Get remaining completed scans after deletion
        const remainingScans = currentCompletedScans.filter(
          (scan) => scan.aiProvider !== aiProvider,
        );

        if (remainingScans.length > 0) {
          // Switch to next available AI provider in same analysis
          setAiProvider(remainingScans[0].aiProvider as AIProviderEnum);
        } else {
          // Find most recent analysis (excluding current one)
          const otherAnalyses = currentAnalysisList.filter(
            (analysis) => analysis.id !== analysisId,
          );

          if (otherAnalyses.length > 0) {
            const mostRecentAnalysis = otherAnalyses.reduce(
              (latest, current) =>
                new Date(current.createdAt || 0) >
                new Date(latest.createdAt || 0)
                  ? current
                  : latest,
            );

            setAnalysisId(mostRecentAnalysis.id);
            navigate(`/brands/${brandId}/analysis/${mostRecentAnalysis.id}`);
          } else {
            // No other analyses - go to brand page with empty state
            setAnalysisId('latest');
            navigate(`/brands/${brandId}`);
          }
        }

        // Refresh cache after all navigation is complete
        await invalidateCache();
      },
    },
  });

  const [showUpgradeToAddModal, setShowUpgradeToAddModal] = useState(false);

  const currentBrand = brands?.find(
    (brand) => String(brand.id) === String(brandId),
  );

  const isAutomaticAnalysisEnabled =
    currentBrand?.settings?.enableAutomaticAnalysis !== false;

  // reduce scans down to an array of AI platform strings
  const aiProviders =
    completedScans.length > 0
      ? completedScans.map((scan) => ({
          value: scan.aiProvider,
          label: getAIProviderLabel(scan.aiProvider),
        }))
      : [{ value: '', label: 'AI platform' }];

  // Initialize AI provider to first available (non-failed) scan if not set or if current provider is unavailable
  useEffect(() => {
    if (completedScans.length > 0) {
      const availableProviders = completedScans.map((s) => s.aiProvider);
      // If no AI provider is set, or if the current provider isn't in the successful scans, set to first one
      if (!aiProvider || !availableProviders.includes(aiProvider)) {
        const firstProvider = completedScans[0].aiProvider;
        if (firstProvider) {
          setAiProvider(firstProvider as AIProviderEnum);
        }
      }
    }
  }, [completedScans, aiProvider, setAiProvider]);

  // Helper: get valid selected analysisId
  const validAnalysisId = analysisList?.some(
    (a) => String(a.id) === String(analysisId),
  )
    ? analysisId
    : analysisList && analysisList.length > 0
      ? analysisList[0].id
      : 'latest';

  const { data: brandData, isLoading: isLoadingBrand } = useGetBrand(brandId, {
    query: {
      enabled: !!brandId,
      select: ({ data }) => data,
    },
  });

  const nextScheduledAnalysis = formatScheduledAnalysisDate(
    brandData?.nextScheduledAnalysis,
  );
  const status = analysisData?.analysis?.status;

  // --- LEGAL ALERT LOGIC ---
  // State to track whether the legal alert has been acknowledged
  const [showLegalAlert, setShowLegalAlert] = useState(false);

  // Check localStorage and status conditions to determine if alert should show
  React.useEffect(() => {
    const currentAnalysisId = analysisData?.analysis?.id;
    const userId = user?.id;

    // Only show alert when analysis is completed and we have valid IDs
    if (
      !brandId ||
      !currentAnalysisId ||
      !aiProvider ||
      !userId ||
      status !== AnalysisStatus.completed
    ) {
      setShowLegalAlert(false);
      return;
    }

    // Generate secure AI provider-specific localStorage key
    const hashedLegalKey = generateHashedLegalKey(
      userId,
      brandId,
      currentAnalysisId,
      aiProvider,
    );

    // Check for backward compatibility with ChatGPT/OpenAI
    let isDismissed = window.localStorage.getItem(hashedLegalKey) !== null;

    if (!isDismissed && aiProvider === 'openai') {
      // Check legacy key for ChatGPT backward compatibility
      const legacyLegalKey = generateLegacyKey(brandId, currentAnalysisId);
      const legacyLegalAlertDismissed =
        window.localStorage.getItem(legacyLegalKey) !== null;

      if (legacyLegalAlertDismissed) {
        // Migrate legacy dismissal to new secure format and clean up
        window.localStorage.setItem(hashedLegalKey, 'true');
        window.localStorage.removeItem(legacyLegalKey);
        isDismissed = true;
      }
    }

    setShowLegalAlert(!isDismissed);
  }, [brandId, analysisData?.analysis?.id, status, aiProvider, user?.id]);

  const handleCloseLegalAlert = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const currentAnalysisId = analysisData?.analysis?.id;
    const userId = user?.id;

    if (!brandId || !currentAnalysisId || !aiProvider || !userId) return;

    // Generate secure key and store dismissal state
    const secureKey = generateHashedLegalKey(
      userId,
      brandId,
      currentAnalysisId,
      aiProvider,
    );
    window.localStorage.setItem(secureKey, 'true');
    setShowLegalAlert(false);
  };

  // Define alert configurations for each status
  const alertConfigMap: Record<
    string,
    { alertContent: React.ReactNode; alertVariant?: string }
  > = {
    [AnalysisStatus.failed]: {
      alertContent: (
        <>
          Analysis failed. Try restarting the analysis or contact support if the
          issue persists.
        </>
      ),
      alertVariant: 'danger',
    },
    [AnalysisStatus.cancelled]: {
      alertContent: (
        <>Analysis was cancelled. You can restart the analysis when ready.</>
      ),
      alertVariant: 'warning',
    },
  };

  // Get alert configuration with fallback
  type AlertKey = keyof typeof alertConfigMap;
  const { alertContent, alertVariant } = alertConfigMap[
    status as AlertKey
  ] ?? {
    alertContent: null,
    alertVariant: 'info',
  };

  // should display notice about brand group changes
  const showOutdatedScanNotice =
    // analysis should be completed
    status === AnalysisStatus.completed &&
    // data is available to show
    selectedScanData &&
    // scan data is either updating or completed
    ['updating', 'completed'].includes(selectedScanData.status) &&
    // scan is not up to date with brand groups
    !selectedScanData.isUpToDateWithBrandGroups;

  // rescanning for brand groups
  const isUpdatingForNewBrandGroups =
    // check for scan to be updating
    selectedScanData?.status === ScanStatus.updating &&
    // and outdated scan notice to be shown
    showOutdatedScanNotice === true;

  const handleAddNewBrand = () => {
    // Check available brand slots
    const maxNumberOfActiveBrands = userResponse?.maxNumberOfActiveBrands ?? 0;
    const numberOfActiveBrands = userResponse?.numberOfActiveBrands ?? 0;
    const availableBrandSlots = maxNumberOfActiveBrands - numberOfActiveBrands;
    const canUserAddNewBrand = availableBrandSlots > 0;

    if (!canUserAddNewBrand) {
      // Show the license modal before allowing new brand creation
      setShowUpgradeToAddModal(true);
      return;
    }

    navigate('/setup');
  };

  // Reusable helper: dismiss Perplexity popover and optionally persist state
  const dismissPerplexityPopover = (saveToLocalStorage: boolean = true) => {
    setPerplexityPopoverDismissed(true);
    if (saveToLocalStorage) {
      localStorage.setItem(PERPLEXITY_TOOLTIP_DISMISSED_KEY, 'true');
    }
  };

  // Reusable helper: dismiss Claude popover and optionally persist state
  const dismissClaudePopover = (saveToLocalStorage: boolean = true) => {
    setClaudePopoverDismissed(true);
    if (saveToLocalStorage) {
      localStorage.setItem(CLAUDE_TOOLTIP_DISMISSED_KEY, 'true');
    }
  };

  return (
    <>
      {/* License Modal */}
      <ModalUpgradeToAdd
        isOpen={showUpgradeToAddModal}
        onClose={() => setShowUpgradeToAddModal(false)}
      />

      {/* Delete Brand Analysis Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={() => {
          if (brandId && analysisId && selectedScanData?.id) {
            deleteScan({ brandId, analysisId, scanId: selectedScanData.id });
          }
        }}
        isLoading={isDeletingScan}
        title={`Delete ${getAIProviderLabel(aiProvider)} analysis?`}
        description={
          <>
            <p>
              This will permanently delete the{' '}
              <b>{getAIProviderLabel(aiProvider)}</b> analysis for{' '}
              <b>
                {selectedScanData?.finishedAt
                  ? format(
                      new Date(selectedScanData.finishedAt),
                      'MMMM d, yyyy',
                    )
                  : 'this date'}
              </b>
              . Analyses from other AI platforms for that date will not be
              affected.
            </p>
            <p>This action cannot be undone.</p>
          </>
        }
        confirmText={`Yes, delete analysis`}
        cancelText="No, go back"
      />

      {/* Failed scan(s) alert */}
      {failedScans.length > 0 && !isAlertDismissed && (
        <IncompleteAnalysisAlert
          failedScans={failedScans}
          brandId={brandId}
          analysisId={analysisId}
          onDismiss={handleIncompleteAlertDismiss}
        />
      )}

      {/* Brand analysis card */}
      <Card className={cx('yst-relative yst-overflow-visible', className)}>
        <Card.Content className="yst-mt-0">
          {/* header */}
          <div className="yst-flex yst-flex-1 yst-flex-col yst-flex-wrap yst-items-start yst-justify-between yst-gap-4 md:yst-flex-row xl:yst-items-center">
            <div className="xl:yst-max-w-1/4">
              <div className="yst-flex yst-flex-col yst-gap-2 lg:yst-gap-2 xl:yst-flex-row xl:yst-items-baseline">
                <Title>
                  <>
                    {isLoadingUser ? (
                      <SkeletonLoader className="yst-inline-block yst-h-8 yst-w-64" />
                    ) : (
                      <>
                        Hi
                        {currentUserDisplayName && ` ${currentUserDisplayName}`}
                        ,
                      </>
                    )}{' '}
                  </>
                </Title>
                <p className="yst-text-sm yst-font-normal yst-text-slate-600">
                  welcome to your brand analysis!
                </p>
              </div>
            </div>

            {/* actions */}
            <div className="yst-flex yst-flex-row yst-flex-wrap yst-items-center yst-justify-end yst-gap-4 yst-self-end md:yst-self-start">
              {/* <Button
                variant="tertiary"
                className="yst-text-red-600"
                onClick={() => setShowDeleteModal(true)}
                disabled={isLoadingAnalysisList || isLoadingBrands}
              >
                <XIcon /> Reset brand analysis
              </Button> */}

              <Button
                variant="primary"
                onClick={handleAddNewBrand}
                disabled={
                  isLoadingAnalysisList ||
                  isLoadingBrands ||
                  // wait for user to provide available licenses
                  isLoadingUser
                }
              >
                <PlusIcon /> Analyze a different brand
              </Button>

              {/* TODO */}
              {/* <Button
                variant="secondary"
                disabled={
                  isLoadingAnalysisList ||
                  isLoadingBrands ||
                  (brands && brands?.length < 1)
                }
              >
                <SparklesIcon /> Run analysis again
              </Button> */}

              {/* TODO */}
              <div className="yst-relative yst-flex yst-items-center">
                <DropdownMenu>
                  <DropdownMenu.IconTrigger
                    screenReaderTriggerLabel="Open brand list options"
                    disabled={
                      isLoadingAnalysisList ||
                      isLoadingBrands ||
                      (brands && brands?.length < 1)
                    }
                  >
                    <DotsVerticalIcon />
                  </DropdownMenu.IconTrigger>
                  <DropdownMenu.List className="yst-absolute yst-right-0 yst-top-full yst-z-40 yst-mt-2 yst-flex yst-w-64 yst-flex-col yst-justify-start">
                    {/* TODO */}
                    {/* <DropdownMenu.ButtonItem
                    variant="secondary"
                    onClick={() => {
                      // Navigate to settings or edit mode
                      console.log('Edit settings and run again...');
                      // You can implement navigation to settings here
                      // navigate('/setup');
                    }}
                    className="yst-flex yst-justify-start"
                  >
                    <AdjustmentsIcon className="yst-h-4 yst-w-4" />
                    Edit settings and run again...
                  </DropdownMenu.ButtonItem> */}
                    <DropdownMenu.ButtonItem
                      className="yst-flex yst-justify-start yst-text-red-600 hover:yst-bg-red-50"
                      onClick={() => setShowDeleteModal(true)}
                      disabled={!selectedScanData}
                    >
                      <TrashIcon className="yst-h-4 yst-w-4" />
                      Delete analysis
                    </DropdownMenu.ButtonItem>
                  </DropdownMenu.List>
                </DropdownMenu>
              </div>
            </div>
          </div>

          <Divider />

          {/* inline form with three fields: - Brand name, - Results from (dropdown with ChatGPT selected by default), Analysis date (read only) */}
          <form
            className={
              // Mobile: flex-col, stretch items; md+: flex-row, align to top so selects stay aligned when one column has extra content (e.g. error message)
              'yst-flex yst-flex-col yst-items-stretch yst-gap-4 md:yst-flex-row md:yst-items-start'
            }
          >
            {/* BRAND */}
            {/* TODO: create a local custom component */}
            <SelectDiv className="yst-relative">
              <Label htmlFor="brand-name">Brand</Label>
              <Select
                onChange={(newBrandId: string) => {
                  navigate(`/brands/${newBrandId}`);
                }}
                id="brand-name"
                value={brandId ? brandId : 'loading'}
                options={
                  brands && brands.length > 0
                    ? brands.map((brand) => ({
                        value: brand.id,
                        label: brand.name,
                      }))
                    : [{ value: 'loading', label: 'Loading...' }]
                }
                disabled={
                  isLoadingBrands || !brands || brands.length < 2 || !brandId
                }
              />
            </SelectDiv>

            {/* ANALYSIS ID - Calendar Picker */}
            <SelectDiv className="yst-relative">
              <Label htmlFor="analysis-date">Analysis date</Label>
              <AnalysisDateSelect
                analyses={analysisList || []}
                selectedAnalysisId={validAnalysisId}
                onAnalysisSelect={(newAnalysisId) => {
                  navigate(
                    `/brands/${brandId}/analysis/${String(newAnalysisId)}`,
                  );
                }}
                isLoading={isLoadingAnalysisList}
                disabled={
                  isLoadingAnalysisList ||
                  !analysisList ||
                  analysisList.length < 1
                }
                id="analysis-date"
              />
            </SelectDiv>

            {/* AI PLATFORM - dependent on the current analysis */}
            <SelectDiv className="yst-relative">
              <div className="yst-flex yst-items-center yst-gap-2">
                <Label htmlFor="ai-tool">AI platform</Label>
              </div>
              <Select
                onChange={(newValue: string) => {
                  // Validate newValue against known AI providers before updating state
                  if (
                    Object.values(AIProviderEnum).includes(
                      newValue as unknown as AIProviderEnum,
                    )
                  ) {
                    setAiProvider(newValue as AIProviderEnum);
                  } else {
                    // Log an error for unexpected values — avoid silently accepting invalid providers
                    // This helps catch issues from unexpected API responses or UI bugs.
                    console.error('Invalid AI provider selected:', newValue);
                  }
                  // if Perplexity is selected, dismiss the popover and persist
                  if (newValue === AIProviderEnum.perplexity) {
                    dismissPerplexityPopover();
                  }
                  // if Claude is selected, dismiss the popover and persist
                  if (newValue === AIProviderEnum.anthropic) {
                    dismissClaudePopover();
                  }
                }}
                id="ai-tool"
                value={aiProvider}
                options={
                  isLoadingAnalysis || isLoadingAnalysisList
                    ? [{ value: aiProvider, label: 'Loading...' }]
                    : aiProviders
                }
                disabled={
                  isLoadingAnalysis ||
                  isLoadingAnalysisList ||
                  // AI platform list array
                  aiProviders.length < 2
                }
                buttonProps={{
                  className: cx('yst-select__button', {
                    'yst-ring-2 yst-ring-primary-500':
                      showPerplexityPopover || showClaudePopover,
                  }),
                }}
              />
              {failedScans.length > 0 && isAlertDismissed && (
                <IncompleteAnalysisMessage
                  failedScans={failedScans}
                  brandId={brandId}
                  analysisId={analysisId}
                />
              )}

              {/* Positioned Popover */}
              <Popover
                id="perplexity-introduction-popover"
                isVisible={showPerplexityPopover}
                // Do not persist dismissal when simply closing via outside click
                onClose={() => dismissPerplexityPopover(false)}
                variant="info"
                delay={2000}
              >
                <div className="yst-space-y-3">
                  {/* Header with icon and title */}
                  <div className="yst-flex yst-items-start yst-gap-2">
                    <YoastIconSmall className="yst-mt-0.5 yst-h-4 yst-w-4 yst-flex-shrink-0" />
                    <div className="yst-flex-1 yst-space-y-1">
                      <Title size="4" className="yst-text-sm yst-font-medium">
                        New: See how your brand appears in Perplexity
                      </Title>

                      {/* Message */}
                      <p className="yst-mb-4 yst-text-sm">
                        Select 'Perplexity' from the{' '}
                        <strong className="yst-font-medium">AI platform</strong>{' '}
                        dropdown to see how your brand appears.
                      </p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="yst-flex yst-justify-end yst-gap-2">
                    <Button
                      variant="tertiary"
                      size="small"
                      // Explicit dismiss should persist
                      onClick={() => dismissPerplexityPopover(true)}
                    >
                      Dismiss
                    </Button>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => {
                        // Set AI provider to Perplexity if available
                        const perplexityProvider = aiProviders.find(
                          (p) => p.value === AIProviderEnum.perplexity,
                        );
                        if (perplexityProvider) {
                          setAiProvider(AIProviderEnum.perplexity);
                        }
                        // Selecting Perplexity should persist dismissal
                        dismissPerplexityPopover(true);
                      }}
                    >
                      Select Perplexity
                    </Button>
                  </div>
                </div>
              </Popover>

              {/* Claude Introduction Popover */}
              <Popover
                id="claude-introduction-popover"
                isVisible={showClaudePopover}
                // Do not persist dismissal when simply closing via outside click
                onClose={() => dismissClaudePopover(false)}
                variant="info"
                delay={2000}
                className="md:yst-left-[-120px] md:yst-translate-x-0"
              >
                <div className="yst-space-y-3">
                  {/* Header with icon and title */}
                  <div className="yst-flex yst-items-start yst-gap-2">
                    <YoastIconSmall className="yst-mt-0.5 yst-h-4 yst-w-4 yst-flex-shrink-0" />
                    <div className="yst-flex-1 yst-space-y-1">
                      <Title size="4" className="yst-text-sm yst-font-medium">
                        New: See how your brand appears in Claude
                      </Title>

                      {/* Message */}
                      <p className="yst-mb-4 yst-text-sm">
                        Select 'Claude' from the{' '}
                        <strong className="yst-font-medium">AI platform</strong>{' '}
                        dropdown to see how your brand appears.
                      </p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="yst-flex yst-justify-end yst-gap-2">
                    <Button
                      variant="tertiary"
                      size="small"
                      // Explicit dismiss should persist
                      onClick={() => dismissClaudePopover(true)}
                    >
                      Dismiss
                    </Button>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => {
                        // Set AI provider to Claude if available
                        const claudeProvider = aiProviders.find(
                          (p) => p.value === AIProviderEnum.anthropic,
                        );
                        if (claudeProvider) {
                          setAiProvider(AIProviderEnum.anthropic);
                        }
                        // Selecting Claude should persist dismissal
                        dismissClaudePopover(true);
                      }}
                    >
                      Select Claude
                    </Button>
                  </div>
                </div>
              </Popover>
            </SelectDiv>
          </form>

          {currentBrand?.setupStatus !== 'pendingQuerySelection' && (
            <>
              <Divider />

              {/* Next analysis status */}
              <div className="yst-flex yst-items-center yst-justify-center yst-gap-2 yst-text-sm yst-text-slate-600">
                {isLoadingBrands || isLoadingBrand ? (
                  <SkeletonLoader className="yst-h-5 yst-w-64" />
                ) : status === AnalysisStatus.processing ||
                  currentBrand?.hasAnalysisInProgress ? (
                  <>
                    <Spinner size="3" />
                    <span>
                      Your analysis is running now, and we'll email you when
                      it's ready.
                    </span>
                  </>
                ) : !isAutomaticAnalysisEnabled ? (
                  <>
                    <span className="yst-inline-block yst-h-2 yst-w-2 yst-rounded-full yst-bg-slate-400" />
                    <span>
                      Automatic analysis is paused &middot;{' '}
                      <Link
                        as={RouterLink}
                        to="/settings"
                        className="yst-font-medium yst-text-primary-500 yst-underline"
                      >
                        Enable in Settings
                      </Link>
                    </span>
                  </>
                ) : brandData?.nextScheduledAnalysis ? (
                  <>
                    <span className="yst-inline-block yst-h-2 yst-w-2 yst-rounded-full yst-bg-green-500" />
                    <span>
                      Your analysis is scheduled for {nextScheduledAnalysis},
                      and we'll email you when it's ready.
                    </span>
                  </>
                ) : (
                  <>
                    <span className="yst-inline-block yst-h-2 yst-w-2 yst-rounded-full yst-bg-slate-400" />
                    <span>Next analysis date will appear here soon</span>
                  </>
                )}
              </div>
            </>
          )}
        </Card.Content>
      </Card>

      <div className="ystaii-alerts yst-space-y-4">
        {/* Legal alert */}
        {showLegalAlert && (
          <Alert>
            <div className="yst-flex yst-w-full">
              <div className="yst-max-w-xl yst-space-y-4">
                <p className="yst-mb-0">
                  {/* https://github.com/Yoast/ai-insights-frontend/issues/197 */}
                  Note: These insights are produced using AI and may contain
                  errors.
                </p>
              </div>
              <div className="yst-ml-auto">
                <Link onClick={handleCloseLegalAlert} variant="default">
                  <XIcon />
                </Link>
              </div>
            </div>
          </Alert>
        )}

        {/* Analysis alert */}
        {status !== AnalysisStatus.completed && alertContent && (
          <Alert className="md:yst-col-span-2" variant={alertVariant}>
            {alertContent}
          </Alert>
        )}

        {/* Retry in progress alert */}

        {status === AnalysisStatus.completed &&
          pendingScans.some((scan) => scan.manualRetryCount >= 1) && (
            <Alert variant="info">
              {(() => {
                const retryingScans = pendingScans.filter(
                  (scan) => scan.manualRetryCount >= 1,
                );
                const providerNames = retryingScans.map((scan) =>
                  getAIProviderLabel(scan.aiProvider),
                );
                return retryingScans.length === 1 ? (
                  <>
                    Retrying the {providerNames[0]} scan — this may take a few
                    minutes.
                  </>
                ) : (
                  <>
                    Retrying the {providerNames.join(' and ')} scans — this may
                    take a few minutes.
                  </>
                );
              })()}
            </Alert>
          )}

        {/* Show an alert if the user has modified their brand groupings */}
        {showOutdatedScanNotice && (
          <Alert variant="warning">
            Note: This scan is not up to date with your current brand groups.
            {isUpdatingForNewBrandGroups ? (
              <>
                {' '}
                We're updating your sentiment analysis to reflect your new brand
                groups. This may take a few minutes.
              </>
            ) : (
              <> Newer scans will use your newly selected brand groupings.</>
            )}
          </Alert>
        )}
      </div>

      {currentBrand && (
        <AlertFinishBrandSetup key={currentBrand.id} brand={currentBrand} />
      )}
      {currentBrand && (
        <AlertStartTrialAnalysis
          key={`trial-${currentBrand.id}`}
          brand={currentBrand}
        />
      )}
    </>
  );
};

export default HeaderCard;
